"use client"

import { ImportProgress } from "./ImportProgress"
import { Note } from "@types"
import { ImportOptions } from "../../types"
import { ImportCompleteModal } from "../ImportCompleteModal/index"
import { BackButton } from "./BackButton"
import { ErrorAlert } from "./ErrorAlert"
import { useEffect, useState } from "react"
import useMirrorImport from "./hooks/useMirrorImport"

interface MirrorImportHandlerProps {
  notes: Partial<Note>[]
  importOptions: ImportOptions
  onComplete?: () => void
  onBack?: () => void
}

/**
 * Component responsible for handling the Mirror post import process.
 * Uses custom hooks for state management and import logic
 */
export function MirrorImportHandler({
  notes,
  importOptions,
  onComplete,
  onBack,
}: MirrorImportHandlerProps) {
  // Import process state and logic
  const {
    importError,
    numFinished,
    skippedPosts,
    erroredPosts,
    importStatus,
    importJobDetails,
    postsRemaining,
  } = useMirrorImport(notes, importOptions)

  // Modal state
  const [showCompleteModal, setShowCompleteModal] = useState(false)

  useEffect(() => {
    // Update modal visibility when import is finished
    if (importStatus === "FINISHED" && importJobDetails && !showCompleteModal) {
      setShowCompleteModal(true)
      if (onComplete) onComplete()
    }
  }, [importStatus])

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        {/* Import progress */}
        <ImportProgress
          numFinished={numFinished}
          skippedPosts={skippedPosts}
          erroredPosts={erroredPosts}
          postsRemaining={postsRemaining}
        />

        {/* Error alert */}
        <ErrorAlert error={importError} />
      </div>

      {/* Button row with Back button */}
      {onBack && (
        <div className="flex items-center gap-3 mt-2">
          <BackButton onClick={onBack} />
        </div>
      )}

      {/* Completion modal */}
      <ImportCompleteModal
        open={showCompleteModal}
        onOpenChange={setShowCompleteModal}
        job={importJobDetails}
        type="posts"
      />
    </div>
  )
}
